import { Injectable } from '@angular/core';
import { ApiService } from './api.service';
import { Pair } from '../models/pair.model';

export interface BroadcastRecord {
  id: string;
  title: string;
  content: string;
  totalUsers: number;
  zquery: string;
  creationDate: number;
}

export interface UserSearchResult {
  id: string;
  name?: string | null;
  email?: string | null;
}

export interface BroadcastResult {
  executionArn: string;
  message: string;
}

export interface NotificationAdminItem {
  id: string;
  owner: string;
  title: string;
  content: string;
  read: boolean;
  creationDate: number;
  readDate?: number;
  userName?: string | null;
  userDisplayName?: string | null;
  userEmail?: string | null;
}

export interface NotificationAdminFilters {
  owner?: string;
  read?: 'true' | 'false' | null;
  from?: number | null;
  to?: number | null;
  nextToken?: string | null;
}

export interface NotificationAdminPage {
  items: NotificationAdminItem[];
  nextToken: string | null;
  count: number;
}

@Injectable({
  providedIn: 'root'
})
export class NotificationAdminService {

  constructor(private apiService: ApiService) {}

  /**
   * Starts a broadcast to ALL users via Step Functions.
   * Returns 202 immediately; the broadcast runs asynchronously.
   */
  async sendBroadcast(title: string, content: string): Promise<BroadcastResult> {
    return this.apiService.doPost('notifications/broadcast', { title, content });
  }

  /**
   * Sends a notification to a single user by UID.
   */
  async sendToUser(title: string, content: string, userId: string): Promise<void> {
    return this.apiService.doPost('notifications', { title, content, owner: userId });
  }

  /**
   * Returns the history of all broadcast sends.
   */
  async getBroadcastHistory(): Promise<BroadcastRecord[]> {
    return this.apiService.doGet('notifications/broadcasts');
  }

  /**
   * Returns a paginated list of all notifications (admin view), optionally filtered.
   */
  async getAllNotifications(filters: NotificationAdminFilters = {}): Promise<NotificationAdminPage> {
    const params: Pair[] = [];
    if (filters.owner) params.push({ key: 'owner', value: filters.owner });
    if (filters.read != null) params.push({ key: 'read', value: filters.read });
    if (filters.from != null) params.push({ key: 'from', value: String(filters.from) });
    if (filters.to != null) params.push({ key: 'to', value: String(filters.to) });
    if (filters.nextToken) params.push({ key: 'nextToken', value: filters.nextToken });
    return this.apiService.doGet('notifications/admin', params.length ? params : undefined);
  }

  /**
   * Searches users by name, email or UID (min 3 chars).
   */
  async searchUsers(q: string): Promise<UserSearchResult[]> {
    const result = await this.apiService.doGet('notifications/admin/users', [{ key: 'q', value: q }]);
    return result.items ?? [];
  }
}
